// Per-custom-field-type capabilities for the automation surfaces. One home so
// the trigger's From/To transition filters, the condition builder's operator
// list, and the "Update field" action agree on what each field type supports.

import { isMultipleField } from "#app/lib/custom-field-utils";

// Which transition filters the "Custom Field changed" trigger offers:
//   fromTo — discrete/scalar types that can name a specific From/To value
//            (select options, members, numbers, money).
//   toOnly — checkbox: only "To" makes sense (Checked / Unchecked / any
//            change); the From is implied.
//   none   — everything else (free text, dates, address, files, photos, …)
//            just fires on any change, with no value filters.
export type FieldChangeFilterMode = "none" | "toOnly" | "fromTo";

export function fieldChangeFilterMode(type: string | undefined): FieldChangeFilterMode {
  switch (type) {
    case "select":
    case "member":
    case "number":
    case "money":
      return "fromTo";
    case "checkbox":
      return "toOnly";
    default:
      return "none";
  }
}

// Types whose value a condition can't meaningfully compare (no editable value
// control, or an opaque object) — the condition builder offers only the
// presence operators (is set / is not set) for these.
export const PRESENCE_ONLY_CONDITION_FIELD_TYPES: ReadonlySet<string> = new Set([
  "files",
  "photos",
  "region",
  "address",
]);

// Types the "Update field" action can't set to a meaningful value — hidden
// from its field picker entirely. (Address stays: it has a full editor.)
export const UNSETTABLE_FIELD_TYPES: readonly string[] = ["files", "photos", "region"];

// Which variable TYPES the insert-variable picker offers for each target
// field type. MIRRORS the server's publish-gate contract in
// apps/api/core/src/automations/catalog/variable-compat.ts — keep in sync.
//
// `null` means free text: any variable stringifies meaningfully. An empty set
// means the type takes no variables at all — its value has structure no
// template can produce (member arrays, address objects, select option ids,
// booleans) — so the insert affordance hides entirely.
const ANY = null;
const NONE: ReadonlySet<string> = new Set();
const NUMERIC: ReadonlySet<string> = new Set(["number", "money"]);
const DATE_LIKE: ReadonlySet<string> = new Set(["date", "dateTime", "time"]);
const EMAIL: ReadonlySet<string> = new Set(["email"]);
const PHONE: ReadonlySet<string> = new Set(["phone"]);
const URL: ReadonlySet<string> = new Set(["url"]);

export function updateFieldVariableTypes(
  fieldType: string | undefined,
): ReadonlySet<string> | null {
  switch (fieldType) {
    case "text":
    case "paragraph":
      return ANY;
    case "number":
    case "money":
      return NUMERIC;
    case "date":
    case "dateTime":
    case "time":
      return DATE_LIKE;
    case "email":
      return EMAIL;
    case "phone":
      return PHONE;
    case "url":
      return URL;
    default:
      return NONE;
  }
}

// The condition operators OFFERED per custom-field type (ClickUp-style), in
// display order. Labels/unary flags live in condition-builder.tsx's operator
// vocabulary; the server accepts the full superset (structured-condition.ts)
// so a saved condition stays evaluable even if a field's type later changes.
// select/member adapt to cardinality: multi-mode adds the membership set
// (any of / all of / …) alongside exact set equality.
export function conditionOperatorsForField(field?: {
  type?: string;
  config?: { multiple?: boolean | null } | null;
} | null): string[] {
  const type = field?.type;
  if (type && PRESENCE_ONLY_CONDITION_FIELD_TYPES.has(type)) return ["isSet", "isNotSet"];
  switch (type) {
    case "checkbox":
      return ["isChecked", "isNotChecked", "isSet", "isNotSet"];
    case "number":
    case "money":
      return [
        "isEqualTo",
        "isNotEqualTo",
        "lessThan",
        "greaterThan",
        "lessThanOrEqual",
        "greaterThanOrEqual",
        "isSet",
        "isNotSet",
      ];
    case "text":
    case "email":
    case "phone":
    case "url":
      return ["isEqualTo", "isNotEqualTo", "contains", "notContains", "isSet", "isNotSet"];
    // Exact equality on long rich text isn't useful — substring + presence.
    case "paragraph":
      return ["contains", "notContains", "isSet", "isNotSet"];
    case "date":
    case "dateTime":
    case "time":
      return ["before", "after", "on", "isSet", "isNotSet"];
    case "select":
    case "member":
      return isMultipleField(field?.config)
        ? [
            "isEqualTo",
            "isNotEqualTo",
            "isAnyOf",
            "isAllOf",
            "isNotAnyOf",
            "isNotAllOf",
            "isSet",
            "isNotSet",
          ]
        : ["isEqualTo", "isNotEqualTo", "isSet", "isNotSet"];
    default:
      return ["isEqualTo", "isNotEqualTo", "isSet", "isNotSet"];
  }
}
